import React, { useState, useRef } from "react";
import { error, success } from "../../../../utils/toast";
import pubsub from "sweet-pubsub";
import axios from "axios";
import { getStoredAuthToken } from "../../../../utils/authToken";
import CancelModal from "../../../../shared/components/CancelModal";
import { ImportStep, ImportMode, MaterialProviderRow, ValidationError } from "./types";
import { validateExcelFile } from "./validation";
import { ImportStepsMaterialProvider } from "./ImportStepsMaterialProvider";

const showToast = (toast: any) => pubsub.emit("toast", toast);

export const ImportMaterialProviderForm = () => {
    const [currentStep, setCurrentStep] = useState<ImportStep>("file");
    const [isUploading, setIsUploading] = useState(false);
    const [importMode, setImportMode] = useState<ImportMode | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [validatedRows, setValidatedRows] = useState<MaterialProviderRow[]>([]);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const [originalRowCount, setOriginalRowCount] = useState<number>(0);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const validExtensions = [".xlsx", ".xls"];
            const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));

            if (!validExtensions.includes(fileExtension)) {
                error("Định dạng file không hợp lệ", "Vui lòng chọn file Excel (.xlsx hoặc .xls)");
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                return;
            }

            setSelectedFile(file);
            // Reset validation state when new file is selected
            setValidatedRows([]);
            setValidationErrors([]);
            setOriginalRowCount(0);
        }
    };

    const handleNext = async () => {
        if (!selectedFile) {
            error("Vui lòng chọn file", "Bạn cần chọn một file Excel để nhập dữ liệu");
            return;
        }

        if (!importMode) {
            error("Vui lòng chọn chế độ nhập", "Bạn cần chọn cách xử lý dữ liệu quan hệ vật tư-nhà cung cấp hiện có");
            return;
        }

        if (currentStep === "file") {
            // Validate file when moving to validation step
            try {
                console.log("Starting validation for file:", selectedFile?.name);
                const { rows, errors, originalRowCount } = await validateExcelFile(selectedFile);
                console.log("Validation completed:", {
                    rowsCount: rows.length,
                    errorsCount: errors.length,
                    originalRowCount,
                    invalidRows: rows.filter(r => !r.isValid).length,
                });
                
                setValidatedRows(rows);
                setValidationErrors(errors);
                setOriginalRowCount(originalRowCount || rows.length);
                
                // Move to validation step to show results
                setCurrentStep("validation");
            } catch (e: any) {
                console.error("Validation error:", e);
                const errorMessage = e?.message || e?.toString() || "Có lỗi xảy ra khi xác thực file";
                showToast({
                    type: "danger",
                    title: "Lỗi validation file",
                    message: errorMessage,
                    duration: 5,
                });
                setSelectedFile(null);
                setValidatedRows([]);
                setValidationErrors([]);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            }
        } else if (currentStep === "validation") {
            // Only allow moving to confirmation if there are no validation errors
            const invalidRowsCount = validatedRows.filter(r => !r.isValid).length;
            if (invalidRowsCount > 0 || validationErrors.length > 0) {
                console.log("Blocking navigation - validation errors:", {
                    invalidRowsCount,
                    validationErrorsCount: validationErrors.length,
                });
                return;
            }
            setCurrentStep("confirmation");
        }
    };

    const handleBack = () => {
        if (currentStep === "validation") {
            setCurrentStep("file");
        } else if (currentStep === "confirmation") {
            setCurrentStep("validation");
        }
    };

    const performImport = async () => {
        if (!selectedFile || !importMode) return;

        setIsUploading(true);

        try {
            const formData = new FormData();
            formData.append("file", selectedFile);

            const token = getStoredAuthToken();
            const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
            const flush = importMode === "flush";

            const response = await axios.post(
                `${baseURL}/utils/upload-material-providers/?flush=${flush}`,
                formData,
                {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : undefined,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data.status === "success") {
                const stats = response.data.stats || {};
                success(
                    "THÀNH CÔNG",
                    `Nhập quan hệ vật tư-nhà cung cấp thành công. Thành công: ${stats.success || 0}, Thất bại: ${stats.failed || 0}`
                );

                // Reset form
                setSelectedFile(null);
                setImportMode(null);
                setValidatedRows([]);
                setValidationErrors([]);
                setOriginalRowCount(0);
                setCurrentStep("file");
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            } else {
                error("Nhập quan hệ vật tư-nhà cung cấp thất bại", response.data.error || "Có lỗi xảy ra");
            }
        } catch (err: any) {
            console.error("Import error:", err);

            if (err.response?.data) {
                const errorData = err.response.data;

                if (errorData.validation_errors) {
                    const errorCount = errorData.invalid_rows || errorData.validation_errors.length;
                    const errorMessages = errorData.validation_errors
                        .slice(0, 5)
                        .map((e: any) => `Dòng ${e.row}: ${e.error || e.field || "Lỗi không xác định"}`)
                        .join("\n");

                    error(
                        `Có ${errorCount} dòng dữ liệu không hợp lệ`,
                        errorMessages + (errorData.validation_errors.length > 5 ? "\n..." : "")
                    );
                } else {
                    error(
                        "Nhập quan hệ vật tư-nhà cung cấp thất bại",
                        errorData.error || errorData.message || err.message || "Có lỗi xảy ra"
                    );
                }
            } else {
                error("Nhập quan hệ vật tư-nhà cung cấp thất bại", err.message || "Có lỗi xảy ra");
            }
        } finally {
            setIsUploading(false);
        }
    };

    const handleImport = () => {
        if (importMode === "flush") {
            setShowConfirmModal(true);
        } else {
            performImport();
        }
    };

    const handleConfirmDelete = () => {
        setShowConfirmModal(false);
        performImport();
    };

    const validRows = validatedRows.filter((r) => r.isValid);

    // Convert MaterialProviderRow[] to ValidationRow[] for generic component
    const validationRows = validatedRows.map((row) => ({
        rowNumber: row.rowNumber,
        data: row.data,
        isValid: row.isValid,
        errors: row.errors,
    }));

    return (
        <div className="tw-p-6 tw-bg-white tw-rounded-lg tw-border-2 tw-border-gray-600 tw-w-full tw-box-border tw-min-w-0">
            <ImportStepsMaterialProvider
                currentStep={currentStep}
                selectedFile={selectedFile}
                importMode={importMode}
                onFileChange={handleFileChange}
                onImportModeChange={setImportMode}
                onNext={handleNext}
                fileInputRef={fileInputRef}
                isUploading={isUploading}
                hasValidationErrors={validationErrors.length > 0}
                validatedRows={validationRows}
                onBack={handleBack}
                onImport={handleImport}
                originalRowCount={originalRowCount}
            />

            <CancelModal
                open={showConfirmModal}
                handleClose={() => setShowConfirmModal(false)}
                handleConfirm={handleConfirmDelete}
                title="Xác nhận xóa tất cả quan hệ vật tư-nhà cung cấp"
                contentText={(() => {
                    const validRowsCount = validRows.length;
                    let content = `Bạn có chắc chắn muốn xóa TẤT CẢ quan hệ vật tư-nhà cung cấp hiện có và thay thế bằng ${validRowsCount} quan hệ từ file?\n\n`;
                    
                    content += "Hành động này sẽ:\n";
                    content += "- Xóa vĩnh viễn tất cả quan hệ vật tư-nhà cung cấp trong hệ thống\n";
                    content += `- Thay thế bằng ${validRowsCount} quan hệ từ file Excel\n`;
                    content += "- Vật tư chưa tồn tại sẽ được tự động tạo mới\n";
                    content += "- Không thể hoàn tác\n\n";
                    content += "Vui lòng xác nhận bạn muốn tiếp tục.";
                    
                    return content;
                })()}
                confirmButtonText="Xác nhận xóa tất cả"
                closeButtonText="Hủy bỏ"
                dangerous={true}
            />
        </div>
    );
};

